import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
} from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { addMoreRollbackTime } from '@openapi/orders/orders';
import useNotify from '../hooks/useNotify';
import {
    formatDateTimeLocal,
    parseDateTimeLocal,
    formatDateTimeInVenueTz,
} from '../helpers/formatDateTime.ts';

function getDiffLabel(isoString, baselineIso) {
    const baseline = baselineIso ? new Date(baselineIso) : new Date();
    const diffMs = new Date(isoString) - baseline;
    const isForward = diffMs >= 0;
    const totalMins = Math.floor(Math.abs(diffMs) / 60000);
    const totalHours = Math.floor(totalMins / 60);
    return {
        d: Math.floor(totalHours / 24),
        h: totalHours % 24,
        m: totalMins % 60,
        isForward,
    };
}

const CustomRollbackTimeModal = ({
    orderId,
    currentRollbackTimeAt,
    onSuccess,
    showModal,
    MODAL_TYPE,
}) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotify();

    const defaultValue = currentRollbackTimeAt ? formatDateTimeLocal(currentRollbackTimeAt) : '';
    const [value, setValue] = useState(defaultValue);
    const [loading, setLoading] = useState(false);

    const nowFormatted = formatDateTimeInVenueTz(new Date());

    const parsedIso = value ? parseDateTimeLocal(value) : '';
    // Diff is shown against the existing rollback time so the operator sees how much
    // they're extending by. The save guard also requires the new time to be in the
    // future (defense-in-depth with the backend) on top of being later than current.
    // TODO: revisit whether the rollback time may extend past the timeslot's start.
    const diff = parsedIso ? getDiffLabel(parsedIso, currentRollbackTimeAt) : null;
    const isAfterNow = parsedIso ? new Date(parsedIso) > new Date() : false;
    const canSave = !!parsedIso && !!diff?.isForward && isAfterNow;

    const closeAction = () => showModal(MODAL_TYPE.NONE);

    const handleSave = async () => {
        if (!canSave) return;
        try {
            setLoading(true);
            await addMoreRollbackTime(orderId, { rollbackTimeAt: parsedIso });
            notifySuccess(t('orders.notifications.rollbackTimeSet'));
            if (onSuccess) onSuccess();
            closeAction();
        } catch (error) {
            notifyError(error?.response?.data?.message || t('errors.unknown'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open onClose={closeAction} maxWidth="xs" fullWidth>
            <DialogTitle>{t('orders.customRollbackModal.title')}</DialogTitle>
            <DialogContent>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                >
                    {t('orders.customRollbackModal.nowLabel')}: {nowFormatted}
                </Typography>
                <TextField
                    label={t('orders.customRollbackModal.targetLabel')}
                    type="datetime-local"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    fullWidth
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                />
                {diff && (
                    <Box sx={{ mt: 1 }}>
                        <Typography
                            variant="caption"
                            color={diff.isForward && isAfterNow ? 'success.main' : 'error.main'}
                        >
                            {diff.isForward ? '+' : '-'}
                            {diff.d > 0 ? `${diff.d}d ` : ''}
                            {diff.h}h {diff.m}m{' '}
                            {currentRollbackTimeAt
                                ? diff.isForward
                                    ? t('orders.customRollbackModal.afterCurrent')
                                    : t('orders.customRollbackModal.beforeCurrent')
                                : diff.isForward
                                  ? t('orders.customRollbackModal.fromNow')
                                  : t('orders.customRollbackModal.ago')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <CommonButton
                    buttonType="secondary"
                    onClick={closeAction}
                    disabled={loading}
                    label={t('common.cancel')}
                />
                <CommonButton
                    buttonType="primary"
                    onClick={handleSave}
                    disabled={loading || !canSave}
                    label={t('common.save')}
                />
            </DialogActions>
        </Dialog>
    );
};

export default CustomRollbackTimeModal;
